import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { QRCodeSVG } from "qrcode.react";
import api from "@/lib/api";
import EntityLogo from "@/components/EntityLogo";
import { toast } from "sonner";
import {
  Printer, Download, ImageDown, Copy, ExternalLink, Shield, MapPin, Globe,
  Instagram, Twitter, Award, GitCompareArrows, Users, TrendingUp, CalendarDays,
} from "lucide-react";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const rk = (v) => (v == null ? "—" : `#${v}`);
const val = (v) => (v == null ? "—" : v);

export default function TeamPortfolio() {
  const { slug } = useParams();
  const [d, setD] = useState(null);
  const [err, setErr] = useState(false);

  useEffect(() => {
    api.get(`/live/teams/${slug}/portfolio`).then(({ data }) => {
      setD(data);
      document.title = `${data.team.name} | Team Portfolio | CBR Live`;
    }).catch(() => setErr(true));
    return () => { document.title = "CBR Prospects"; };
  }, [slug]);

  if (err) return <Centered>Team portfolio not available.</Centered>;
  if (!d) return <Centered>Loading team portfolio…</Centered>;

  const t = d.team;
  const rt = d.rating || {};
  const rec = d.record || {};
  const st = d.stats || {};
  const ri = d.recruiting_impact || {};
  const pdfUrl = `${BACKEND}/api/live/teams/${t.id}/portfolio.pdf`;
  const cardUrl = `${BACKEND}/api/live/teams/${t.id}/portfolio-card.png`;
  const copy = () => { navigator.clipboard?.writeText(d.portfolio_url); toast.success("Team portfolio link copied"); };

  return (
    <div className="min-h-screen bg-[#F1F3F6]" data-testid="team-portfolio-page">
      {/* Toolbar */}
      <div className="print:hidden sticky top-0 z-20 bg-[#0A192F] text-white border-b border-white/10">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 py-3 flex flex-wrap items-center gap-2">
          <span className="font-display uppercase tracking-tight text-sm mr-auto flex items-center gap-2">
            <span className="bg-[#E63946] px-1.5 py-0.5 text-xs">CBR</span> Team Portfolio
          </span>
          <ToolBtn testid="team-portfolio-print" onClick={() => window.print()} icon={Printer}>Print</ToolBtn>
          <ToolBtn testid="team-portfolio-pdf" href={pdfUrl} icon={Download}>PDF</ToolBtn>
          <ToolBtn testid="team-portfolio-card" href={cardUrl} icon={ImageDown}>Share Card</ToolBtn>
          <ToolBtn testid="team-portfolio-copy" onClick={copy} icon={Copy}>Copy Link</ToolBtn>
          <ToolBtn testid="team-portfolio-compare" to={`/live/compare?a=${t.id}`} icon={GitCompareArrows}>Compare</ToolBtn>
          <ToolBtn testid="team-portfolio-view" to={`/live/teams/${t.slug}`} icon={ExternalLink}>Team Page</ToolBtn>
        </div>
      </div>

      <div className="max-w-5xl mx-auto p-3 sm:p-6 print:p-0 print:max-w-none">
        <div className="bg-white shadow-xl print:shadow-none">
          {/* Header */}
          <div className="relative bg-[#0A192F] text-white p-6 sm:p-8">
            <div className="absolute top-0 left-0 right-0 h-1 bg-[#E63946]" />
            <div className="flex flex-col sm:flex-row gap-6 items-center sm:items-start">
              <EntityLogo src={t.effective_logo_url || t.logo_url} alt={t.name} size="w-28 h-28" rounded="rounded-sm" textSize="text-2xl" variant="dark" className="border-2 border-white" />
              <div className="flex-1 min-w-0 text-center sm:text-left">
                <div className="label-overline text-[#E63946]">{d.organization?.name || "CBR Live"} · Team Portfolio</div>
                <h1 data-testid="team-portfolio-name" className="font-display text-4xl sm:text-5xl uppercase tracking-tighter mt-1 leading-none">{t.name}</h1>
                <div className="mt-3 flex flex-wrap justify-center sm:justify-start gap-x-5 gap-y-1 text-sm text-white/80">
                  {t.age_division && <span className="font-data">{t.age_division}</span>}
                  {t.league && <span>{t.league}</span>}
                  {t.province && <span className="flex items-center gap-1"><MapPin className="w-3.5 h-3.5" />{t.city_region ? `${t.city_region}, ` : ""}{t.province}</span>}
                  {d.season?.name && <span>{d.season.name}</span>}
                </div>
                <div className="mt-2 flex flex-wrap justify-center sm:justify-start gap-x-4 gap-y-1 text-xs text-white/60">
                  {t.home_diamond && <span>🏟 {t.home_diamond}</span>}
                  {t.website && <a href={t.website} target="_blank" rel="noreferrer" className="flex items-center gap-1 hover:text-white"><Globe className="w-3 h-3" /> Website</a>}
                  {t.social?.instagram && <a href={t.social.instagram} target="_blank" rel="noreferrer" className="flex items-center gap-1 hover:text-white"><Instagram className="w-3 h-3" /> Instagram</a>}
                  {t.social?.x && <a href={t.social.x} target="_blank" rel="noreferrer" className="flex items-center gap-1 hover:text-white"><Twitter className="w-3 h-3" /> X</a>}
                </div>
              </div>
            </div>
          </div>

          {/* Rating + rankings strip */}
          <div className="grid grid-cols-2 sm:grid-cols-5 border-b border-slate-200">
            <div className="bg-[#0A192F] text-white p-4 col-span-2 sm:col-span-1" data-testid="team-portfolio-rating">
              <div className="label-overline text-[#E63946] text-[10px]">CBR Team Rating™</div>
              <div className="font-data text-4xl mt-1">{rt.cbr_team_rating != null ? rt.cbr_team_rating : "—"}</div>
            </div>
            <RankCell label="National" v={rt.national_rank} />
            <RankCell label={t.province || "Provincial"} v={rt.provincial_rank} />
            <RankCell label="Highest" v={rt.highest_rank_season} />
            <div className="p-4 border-l border-slate-100">
              <div className="label-overline text-[10px]">Confidence</div>
              <div className="font-data text-lg text-[#0A192F] mt-1 capitalize">{rt.confidence || "low"}</div>
              <div className="text-[10px] text-slate-400 flex items-center gap-1"><TrendingUp className="w-3 h-3" />{rt.trend || "flat"} · {rt.games_played || 0} GP</div>
            </div>
          </div>

          <div className="p-6 sm:p-8 space-y-8">
            <Section icon={Award} label="Record" title="Team Record">
              <div className="grid grid-cols-3 sm:grid-cols-6 gap-3" data-testid="tp-record">
                <Stat label="Overall" v={recStr(rec.overall)} big />
                <Stat label="League" v={recStr(rec.league)} />
                <Stat label="Tournament" v={recStr(rec.tournament)} />
                <Stat label="Home" v={recStr(rec.home)} />
                <Stat label="Away" v={recStr(rec.away)} />
                <Stat label="Streak" v={rec.current_streak ?? 0} />
              </div>
            </Section>

            <Section icon={TrendingUp} label="Performance" title="Team Statistics">
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="tp-stats">
                <Stat label="Runs Scored" v={st.runs_scored ?? 0} />
                <Stat label="Runs Allowed" v={st.runs_allowed ?? 0} />
                <Stat label="Team AVG" v={val(st.team_avg)} />
                <Stat label="OPS" v={val(st.ops)} />
                <Stat label="ERA" v={val(st.era)} />
                <Stat label="WHIP" v={val(st.whip)} />
                <Stat label="Fielding %" v={val(st.fielding_pct)} />
                <Stat label="HR" v={st.hr ?? 0} />
              </div>
              <p className="text-[11px] text-slate-400 mt-2">Statistics populate automatically as games are scored.</p>
            </Section>

            <Section icon={Users} label="Recruiting" title="Recruiting Impact">
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="tp-recruiting">
                <Stat label="Commitments" v={ri.commitments ?? 0} big />
                <Stat label="Drafted" v={ri.drafted ?? 0} />
                <Stat label="Top 100" v={ri.top100 ?? 0} />
                <Stat label="Avg CBR Rating" v={val(ri.avg_cbr_rating)} />
                <Stat label="Recruiter Interest" v={ri.total_recruiter_interest ?? 0} />
                <Stat label="Profile Views" v={ri.total_profile_views ?? 0} />
                <Stat label="Portfolio Views" v={ri.total_portfolio_views ?? 0} />
              </div>
            </Section>

            {d.roster?.length > 0 && (
              <Section icon={Users} label="Squad" title={`Roster (${d.roster.length})`}>
                <div className="grid sm:grid-cols-2 gap-2" data-testid="tp-roster">
                  {d.roster.map((p) => (
                    <Link key={p.player_id} to={`/prospects/${p.slug || p.player_id}`} data-testid={`tp-roster-${p.player_id}`}
                      className="flex items-center gap-3 p-2 border border-slate-100 rounded-sm hover:border-[#E63946]">
                      <div className="w-9 h-9 rounded-sm bg-slate-100 overflow-hidden grid place-items-center shrink-0">
                        {p.profile_photo_url ? <img src={p.profile_photo_url} alt="" className="w-full h-full object-cover" /> : <span className="text-[10px] text-slate-400">{p.first_name?.[0]}{p.last_name?.[0]}</span>}
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="text-sm font-semibold truncate">{p.number ? `#${p.number} ` : ""}{p.first_name} {p.last_name}</div>
                        <div className="text-[11px] text-slate-400 truncate">{[p.primary_position, p.graduation_year, p.commitment].filter(Boolean).join(" · ")}</div>
                      </div>
                      {p.cbr_overall_rating != null && <span className="font-data text-sm text-slate-500">{p.cbr_overall_rating}</span>}
                    </Link>
                  ))}
                </div>
              </Section>
            )}

            {(d.schedule?.live?.length || d.schedule?.upcoming?.length || d.schedule?.past?.length) ? (
              <Section icon={CalendarDays} label="Schedule" title="Games">
                <div className="space-y-2" data-testid="tp-schedule">
                  {d.schedule.live.map((g) => <GameRow key={g.id} g={g} kind="live" />)}
                  {d.schedule.upcoming.map((g) => <GameRow key={g.id} g={g} kind="upcoming" />)}
                  {d.schedule.past.map((g) => <GameRow key={g.id} g={g} kind="final" />)}
                </div>
              </Section>
            ) : null}

            {d.accomplishments?.length > 0 && (
              <Section icon={Award} label="Recognition" title="Accomplishments">
                <ul className="space-y-2" data-testid="tp-accomplishments">
                  {d.accomplishments.map((a, i) => (
                    <li key={i} className="flex items-start gap-2 text-sm text-slate-700">
                      <Award className="w-4 h-4 text-[#E63946] shrink-0 mt-0.5" />
                      <span><b>{a.title}</b>{a.season ? <span className="text-slate-400"> — {a.season}</span> : null}{a.placement ? ` · ${a.placement}` : ""}</span>
                    </li>
                  ))}
                </ul>
              </Section>
            )}

            {/* QR */}
            <div className="grid sm:grid-cols-3 gap-6 pt-2 border-t border-slate-200">
              <div className="sm:col-span-2 flex items-center">
                <Link to="/live/compare" data-testid="tp-compare-cta" className="inline-flex items-center gap-2 bg-[#0A192F] text-white px-5 py-3 text-sm font-semibold uppercase tracking-wider rounded-sm hover:bg-[#1a2a4a] transition-colors print:hidden">
                  <GitCompareArrows className="w-4 h-4" /> Compare This Team
                </Link>
              </div>
              <div className="text-center sm:text-right">
                <div className="label-overline text-[#E63946] mb-2">Scan for Live Team Page</div>
                <div className="inline-block bg-white p-2 border border-slate-200 rounded-sm" data-testid="tp-qr">
                  <QRCodeSVG value={d.profile_url} size={120} fgColor="#0A192F" />
                </div>
                <div className="font-mono text-[10px] text-slate-500 mt-2 break-all">{(d.profile_url || "").replace(/^https?:\/\//, "")}</div>
              </div>
            </div>
          </div>

          <div className="bg-[#0A192F] text-white/50 text-[10px] uppercase tracking-wider px-6 sm:px-8 py-3 text-center">
            CBR Live · Canadian Baseball · Team Portfolio
          </div>
        </div>
      </div>
    </div>
  );
}

const recStr = (r) => { r = r || { w: 0, l: 0, t: 0 }; return `${r.w || 0}-${r.l || 0}${r.t ? `-${r.t}` : ""}`; };

function ToolBtn({ children, icon: Icon, onClick, href, to, testid }) {
  const cls = "inline-flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider bg-white/10 hover:bg-white/20 px-3 py-2 rounded-sm transition-colors";
  if (to) return <Link to={to} data-testid={testid} className={cls}><Icon className="w-3.5 h-3.5" />{children}</Link>;
  if (href) return <a href={href} target="_blank" rel="noreferrer" onClick={onClick} data-testid={testid} className={cls}><Icon className="w-3.5 h-3.5" />{children}</a>;
  return <button onClick={onClick} data-testid={testid} className={cls}><Icon className="w-3.5 h-3.5" />{children}</button>;
}
function Section({ icon: Icon, label, title, children }) {
  return (
    <div>
      <div className="label-overline text-[#E63946] flex items-center gap-1.5"><Icon className="w-4 h-4" />{label}</div>
      <h2 className="font-display text-2xl uppercase tracking-tight mt-1 mb-4">{title}</h2>
      {children}
    </div>
  );
}
function Stat({ label, v, big }) {
  return (
    <div className="border border-slate-200 rounded-sm p-3">
      <div className="label-overline text-[10px] truncate">{label}</div>
      <div className={`font-data text-[#0A192F] mt-0.5 ${big ? "text-2xl" : "text-xl"}`}>{v}</div>
    </div>
  );
}
function RankCell({ label, v }) {
  return (
    <div className="p-4 border-l border-slate-100">
      <div className="label-overline text-[10px] truncate">{label}</div>
      <div className="font-data text-2xl text-[#0A192F] mt-1">{rk(v)}</div>
    </div>
  );
}
function GameRow({ g, kind }) {
  const show = kind !== "upcoming";
  return (
    <Link to={`/live/games/${g.id}`} className="flex items-center gap-3 border border-slate-100 rounded-sm p-2.5 hover:border-[#E63946]">
      <div className="w-14 text-center shrink-0">
        {kind === "live" ? <span className="text-[9px] font-bold text-white bg-[#E63946] px-1.5 py-0.5 rounded-sm">LIVE</span>
          : kind === "final" ? <span className="text-[10px] text-slate-500 font-semibold">FINAL</span>
          : <span className="text-[11px] text-slate-400">{(g.date || "").slice(5)}</span>}
      </div>
      <div className="flex-1 min-w-0 text-sm">
        <div className="flex justify-between"><span className="truncate">{g.team?.name}</span>{show && <span className="font-data text-slate-500">{g.score?.home ?? 0}</span>}</div>
        <div className="flex justify-between text-slate-500"><span className="truncate">{g.opponent?.name}</span>{show && <span className="font-data">{g.score?.away ?? 0}</span>}</div>
      </div>
    </Link>
  );
}
function Centered({ children }) {
  return <div className="min-h-screen grid place-items-center bg-[#F1F3F6]"><div className="label-overline text-slate-500">{children}</div></div>;
}
